import axios from 'axios';

// Determine Base URL
// Development: explicit backend URL. Production: same origin '' (backend serves frontend at /)
// or set REACT_APP_API_URL e.g. to /api if nginx proxies API under /api.
const baseURL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000'
    : (process.env.REACT_APP_API_URL ?? '');

const api = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const API_ERROR_MESSAGES_RU = {
    INVALID_TOKEN: 'Недействительный токен.',
    INVALID_TOKEN_PAYLOAD: 'Некорректные данные токена.',
    USER_NOT_FOUND: 'Пользователь не найден.',
    INVALID_EMAIL_OR_PASSWORD: 'Неверный email или пароль.',
    INVALID_VERIFICATION_CODE: 'Неверный код подтверждения.',
    VERIFICATION_CODE_EXPIRED: 'Срок действия кода истек.',
    TOO_MANY_VERIFICATION_ATTEMPTS: 'Слишком много попыток. Запросите новый код.',
    EMAIL_ALREADY_VERIFIED: 'Email уже подтвержден.',
    EMAIL_NOT_VERIFIED: 'Email не подтвержден. Подтвердите почту, чтобы продолжить.',
    INVALID_OR_EXPIRED_TOKEN: 'Недействительный или просроченный токен.',
    TOKEN_NOT_FOUND: 'Токен не найден.',
    TOKEN_NOT_FOUND_OR_EXPIRED: 'Токен не найден или просрочен.',
    POLL_NOT_FOUND: 'Опрос не найден.',
    QUESTION_NOT_FOUND: 'Вопрос не найден.',
    POLL_IS_NOT_ACTIVE: 'Опрос не активен.',
    POLL_ALREADY_ANSWERED: 'Вы уже проходили этот опрос.',
    RESPONSE_CREATION_FAILED: 'Не удалось сохранить ответ.',
    ACCESS_DENIED: 'Доступ запрещен.',
    NOT_AUTHORIZED: 'Недостаточно прав.',
    TELEGRAM_WEB_AUTH_DISABLED: 'Функционал организатора в Telegram отключён. Используйте веб-версию для создания и управления опросами.',
    EMAIL_DOMAIN_NOT_ALLOWED: 'Регистрация доступна только с почтовым адресом в зоне .ru или .рф.',
};

export const getApiErrorPayload = (error) => {
    const data = error?.response?.data || {};
    if (typeof data === 'string') {
        return {
            code: null,
            message: data,
            detail: data,
        };
    }
    return {
        code: data.code || null,
        message: data.message || null,
        detail: data.detail || null,
    };
};

export const getApiErrorMessage = (error, fallbackMessage = 'Произошла ошибка') => {
    const payload = getApiErrorPayload(error);
    if (payload.code && API_ERROR_MESSAGES_RU[payload.code]) {
        return API_ERROR_MESSAGES_RU[payload.code];
    }
    return payload.message || payload.detail || fallbackMessage;
};

// Request Interceptor: Add Token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

const HTML_INSTEAD_OF_DATA =
    'Сервер ответил страницей сайта вместо данных. Обычно так бывает, если после обновления сайта не перезапустили сервер (showbot-backend).';

// Неизвестный серверу адрес возвращает страницу сайта (index.html) с кодом 200.
// Так бывает, когда собран новый фронтенд, а сервер ещё на старой версии: считаем это ошибкой,
// чтобы страница показала понятное сообщение, а не упала на чтении полей из HTML.
const isHtmlInsteadOfData = (response) => {
    const type = String(response.headers?.['content-type'] || '');
    const expectsData = !response.config?.responseType || response.config.responseType === 'json';
    return expectsData && type.includes('text/html');
};

// Response Interceptor: Handle Auth Errors
api.interceptors.response.use(
    (response) => {
        if (isHtmlInsteadOfData(response)) {
            const error = new Error(HTML_INSTEAD_OF_DATA);
            error.response = { ...response, data: { detail: HTML_INSTEAD_OF_DATA } };
            error.normalized = getApiErrorPayload(error);
            return Promise.reject(error);
        }
        return response;
    },
    (error) => {
        error.normalized = getApiErrorPayload(error);
        if (error.response && error.response.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default api;
